/**
 * Clean filename by removing storage prefixes (timestamp-randomstring-)
 * Example: "1769718751333-u9uxn2-day-dusk-commercial-after" → "day-dusk-commercial-after"
 * Example: "1769980045092-xbd3z4-day-dusk-commercial-before.jpg" → "day-dusk-commercial-before.jpg"
 */
export const cleanFileName = (filename: string): string => {
  if (!filename) return '';
  
  // Match pattern: timestamp-randomstring- at the start
  // Matches: digits-alphanumeric-randomstring- followed by the actual filename
  // This captures everything after the first two dashes and their prefixes
  
  // Try pattern 1: digits-alphanumeric-rest (most common)
  let match = filename.match(/^\d+-[a-z0-9]+-(.+)$/i);
  
  if (match && match[1]) {
    return match[1];
  }
  
  // Try pattern 2: in case there are more than 2 dashes at the start
  match = filename.match(/^\d+-[a-z0-9]+-[a-z0-9]+-(.+)$/i);
  
  if (match && match[1]) {
    return match[1];
  }
  
  // If no match, return original filename
  return filename;
};

/**
 * Get clean filename for display purposes
 */
export const getDisplayFileName = (filename: string): string => {
  return cleanFileName(filename);
};

/**
 * Get clean filename for download purposes
 */
export const getDownloadFileName = (filename: string): string => {
  return cleanFileName(filename);
};

/**
 * Sanitize filename by removing special characters that cause filesystem issues
 * Preserves alphanumeric, dots, dashes, underscores, and spaces
 */
export const sanitizeFileName = (filename: string): string => {
  if (!filename) return '';
  
  // Split into name and extension
  const lastDotIndex = filename.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0 && lastDotIndex < filename.length - 1;
  
  const name = hasExtension ? filename.substring(0, lastDotIndex) : filename;
  const ext = hasExtension ? filename.substring(lastDotIndex) : '';
  
  // Remove special characters from name (keep alphanumeric, dash, underscore, space)
  const sanitizedName = name.replace(/[^a-zA-Z0-9\-_ ]/g, '');
  
  // Remove special characters from extension (keep alphanumeric and dot)
  const sanitizedExt = ext.replace(/[^a-zA-Z0-9.]/g, '');
  
  return sanitizedName + sanitizedExt;
};

/**
 * Deduplicate filenames by appending numeric suffixes
 * Example: ["photo.jpg", "photo.jpg", "photo.jpg"] → ["photo.jpg", "photo_1.jpg", "photo_2.jpg"]
 */
export const deduplicateFilenames = (filenames: string[]): string[] => {
  const seen = new Map<string, number>();
  const result: string[] = [];
  
  for (const filename of filenames) {
    const count = seen.get(filename) || 0;
    
    if (count === 0) {
      // First occurrence - use original name
      result.push(filename);
      seen.set(filename, 1);
    } else {
      // Duplicate - append suffix
      const lastDotIndex = filename.lastIndexOf('.');
      const hasExtension = lastDotIndex > 0 && lastDotIndex < filename.length - 1;
      
      if (hasExtension) {
        const name = filename.substring(0, lastDotIndex);
        const ext = filename.substring(lastDotIndex);
        result.push(`${name}_${count}${ext}`);
      } else {
        result.push(`${filename}_${count}`);
      }
      
      seen.set(filename, count + 1);
    }
  }
  
  return result;
};

/**
 * Clean, sanitize, and deduplicate a list of filenames
 * This is the main utility function for preparing filenames for ZIP archives
 */
export const prepareFilenamesForZip = (filenames: string[]): string[] => {
  // Step 1: Clean timestamp prefixes
  const cleaned = filenames.map(cleanFileName);
  
  // Step 2: Sanitize special characters
  const sanitized = cleaned.map(sanitizeFileName);
  
  // Step 3: Deduplicate
  const deduplicated = deduplicateFilenames(sanitized);
  
  return deduplicated;
};
